import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase.ts';
import { useStaff } from '../../contexts/StaffContext.tsx';
import { formatMoney, formatDuration } from '../../lib/format.ts';
import type { TenantStats } from '../../lib/types.ts';

async function fetchStats(
  tenantSlug: string,
  locationLocalId: string | null,
  from: string | null,
  to: string | null,
): Promise<TenantStats> {
  const { data, error } = await supabase.rpc('get_tenant_stats', {
    p_tenant_slug: tenantSlug,
    p_location_local_id: locationLocalId,
    p_from: from,
    p_to: to,
  });
  if (error) throw error;
  return data as TenantStats;
}

const PERIOD_OPTIONS = [
  { label: 'Сегодня', days: 0 },
  { label: '7 дней', days: 6 },
  { label: '30 дней', days: 29 },
];

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function StatsPage() {
  const { activeMembership } = useStaff();
  const [periodDays, setPeriodDays] = useState(6);

  const tenantSlug = activeMembership?.tenant_slug ?? '';
  const locationLocalId = activeMembership?.locations?.[0]?.local_id ?? null;

  const now = new Date();
  const from = toDateStr(new Date(now.getTime() - periodDays * 86_400_000));
  const to = toDateStr(now);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['stats', tenantSlug, locationLocalId, from, to],
    queryFn: () => fetchStats(tenantSlug, locationLocalId, from, to),
    enabled: !!tenantSlug,
    staleTime: 5 * 60_000,
  });

  if (!activeMembership) return null;

  const currency = 'RUB';

  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
      <header
        className="sticky top-0 z-20 px-4 py-3"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
      >
        <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
          Статистика — {activeMembership.tenant_name}
        </h1>

        {/* Period tabs */}
        <div className="mt-2 flex gap-1.5">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setPeriodDays(opt.days)}
              className="rounded-full px-3 py-1 text-xs font-medium transition-colors"
              style={{
                background: periodDays === opt.days ? 'var(--accent)' : 'var(--n-100)',
                color: periodDays === opt.days ? '#fff' : 'var(--text-muted)',
                minHeight: 32,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 p-4 flex flex-col gap-4">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-xl" style={{ background: 'var(--surface)' }} />
            ))}
          </div>
        )}

        {isError && (
          <div
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: 'oklch(97% 0.015 25)', color: 'var(--danger)' }}
          >
            {(error as Error)?.message ?? 'Ошибка загрузки статистики'}
          </div>
        )}

        {data && (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Выручка" value={formatMoney(data.collected_total, currency)} />
              <StatCard label="Принято заказов" value={String(data.accepted_orders)} />
              <StatCard label="Выдано" value={String(data.completed_orders)} />
              <StatCard
                label="Отмены"
                value={`${data.cancelled_orders} (${Math.round(data.cancel_rate * 100)}%)`}
                danger={data.cancel_rate > 0.15}
              />
              <StatCard label="Ожидается" value={formatMoney(data.pending_total, currency)} muted />
              <StatCard label="Средний чек" value={formatMoney(data.avg_ticket, currency)} />
              <StatCard
                label="Среднее время"
                value={data.avg_prep_minutes > 0 ? formatDuration(Math.round(data.avg_prep_minutes)) : '—'}
              />
            </div>

            {/* Orders by hour bar chart */}
            {data.orders_by_hour.length > 0 && (
              <section
                className="rounded-xl p-4"
                style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
              >
                <h2 className="mb-3 text-sm font-semibold" style={{ color: 'var(--text)' }}>
                  Заказы по часам
                </h2>
                <HourChart data={data.orders_by_hour} />
              </section>
            )}

            {/* Top items */}
            {data.top_items.length > 0 && (
              <section
                className="rounded-xl p-4"
                style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
              >
                <h2 className="mb-3 text-sm font-semibold" style={{ color: 'var(--text)' }}>
                  Топ позиций
                </h2>
                <div className="flex flex-col gap-2">
                  {data.top_items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span
                        className="text-xs font-bold w-5 text-right flex-shrink-0 tabular-nums"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm truncate" style={{ color: 'var(--text)' }}>
                        {item.name}
                      </span>
                      <span
                        className="text-xs tabular-nums flex-shrink-0"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        {item.quantity} шт
                      </span>
                      <span
                        className="text-xs tabular-nums flex-shrink-0 font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        {formatMoney(item.revenue, currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({
  label, value, muted, danger,
}: { label: string; value: string; muted?: boolean; danger?: boolean }) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
    >
      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
        {label}
      </p>
      <p
        className="text-base font-semibold tabular-nums leading-tight"
        style={{
          color: danger ? 'var(--danger)' : muted ? 'var(--text-muted)' : 'var(--text)',
        }}
      >
        {value}
      </p>
    </div>
  );
}

function HourChart({ data }: { data: Array<{ hour: number; orders: number }> }) {
  const max = Math.max(...data.map((d) => d.orders), 1);

  // Fill all 24 hours so gaps are obvious.
  const filled = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    orders: data.find((d) => d.hour === h)?.orders ?? 0,
  }));

  return (
    <div
      className="flex items-end gap-px overflow-x-auto scrollbar-none"
      style={{ height: 80 }}
      role="img"
      aria-label="График заказов по часам"
    >
      {filled.map(({ hour, orders }) => (
        <div
          key={hour}
          className="flex flex-1 flex-col items-center justify-end gap-0.5"
          style={{ minWidth: 12 }}
          title={`${hour}:00 — ${orders} зак.`}
        >
          <div
            className="w-full rounded-t"
            style={{
              height: `${Math.max((orders / max) * 60, orders > 0 ? 4 : 0)}px`,
              background: 'var(--accent)',
              opacity: orders === 0 ? 0.15 : 1,
            }}
          />
          {hour % 6 === 0 && (
            <span className="text-[9px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
              {hour}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
